import {ActivatedRoute, Router} from '@angular/router';

import {Subscription} from 'rxjs';

import {
    ApiError,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    ClientUserTypesEnum,
    ClientUserStatusesEnum,
    ReportsService,
    ClientUserReportInterface,
    ClientUserFilterModel,
    ClientUserReportModel
} from '@spa/api';

import {PanelPaths} from '../../../shared/paths';
import {Pagination} from '../../../shared/components';
import {ItemsPerPageSelector} from '../../../shared/widgets';
import {AlertsService, CacheCollectionService} from '../../../shared/services';

import {EventsService} from '../services';
import {ClientUsersResolverDataInterface} from '../interfaces';
import {ClientUsersReportResolverErrorStatesEnum} from '../enums';

import {ReportComponent} from './report-component';

import {ClientUsersMessages} from '../../../../assets/messages';
import {InitialComponentDataValidationException} from '../../../shared/exceptions';

export abstract class ClientUsersReportComponent extends ReportComponent<ClientUserReportInterface, ClientUserReportModel> {

    private activatedRouteSubscription: Subscription;

    protected activatedRoute: ActivatedRoute;
    protected reportsService: ReportsService;

    public panelPaths: typeof PanelPaths;

    protected constructor(
        alertsService: AlertsService,
        activatedRouter: ActivatedRoute,
        reportsService: ReportsService,
        router: Router,
        cacheCollectionService: CacheCollectionService,
        eventsService: EventsService
    ) {
        super(router, eventsService, cacheCollectionService);

        this.activatedRoute = activatedRouter;
        this.reportsService = reportsService;

        this.panelPaths = PanelPaths;
        this.filterModel = this.getInitialFilterModel();
    }

    onInit(): void {
        super.onInit();

        this.activatedRouteSubscription = this.activatedRoute.data
            .subscribe(
                (data: { resolverData: ServerResponseData<ClientUsersResolverDataInterface> }) => {
                    this.doInitialDataValidationCheck(data.resolverData, () => {
                        this.filterControls =
                            (data.resolverData.data as ClientUsersResolverDataInterface).filterControls;

                        this.dataTableConfig =
                            (data.resolverData.data as ClientUsersResolverDataInterface).dataTableConfig;

                        this.modelsCollection =
                            (data.resolverData.data as ClientUsersResolverDataInterface).modelsCollection;
                        const start = Pagination.calculateStartPagesChunkIndex(this.paginationConfig.currentPage,
                            this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                        const end = Pagination.calculateEndPagesChunkIndex(this.paginationConfig.currentPage,
                            this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                        const paginatedCollection = this.paginateCollection<ClientUserReportModel>(this.modelsCollection, start, end);

                        this.paginationConfig.totalItems = this.modelsCollection.length;

                        this.dataTableConfig.rows = this.createDataSource(paginatedCollection);
                    });
                });
    }

    onDestroy(): void {
        this.activatedRouteSubscription.unsubscribe();

        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    getReportHandler(filter: ClientUserFilterModel): ServerResponse<ClientUserReportInterface[]> {
        return this.reportsService.getClientUsersReport(filter);
    }

    getFilterModel(data: { [p: string]: number | string | boolean }): ClientUserFilterModel {
        const itemsPerPage = data[ItemsPerPageSelector] as number;
        const itemsPerPagesChunk = Pagination.calculateItemsPerPagesChunk(itemsPerPage);

        const status = data[ClientUserFilterModel.getStatusSelector()] as number;

        let isClientUserLegalRepresentative: boolean = null;

        switch (data[ClientUserFilterModel.getIsLegalRepresentativeSelector()]) {
            case ClientUserTypesEnum.AuthorizedPerson:
                isClientUserLegalRepresentative = false;
                break;
            case ClientUserTypesEnum.LegalRepresentative:
                isClientUserLegalRepresentative = true;
                break;
        }

        return new ClientUserFilterModel(
            (status !== ClientUserStatusesEnum.All) ? status : null,
            data[ClientUserFilterModel.getNameSelector()] as string,
            data[ClientUserFilterModel.getAuthorityNameSelector()] as string,
            data[ClientUserFilterModel.getEmailSelector()] as string,
            data[ClientUserFilterModel.getPhoneNumberSelector()] as string,
            isClientUserLegalRepresentative,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            Pagination.defaultPagesChunk,
            itemsPerPagesChunk,
            ClientUserReportModel.getCustomerIdSelector(),
            false
        );
    }

    mapFilterResults(results: ClientUserReportInterface[]): ClientUserReportModel[] {
        return results.map((report: ClientUserReportInterface) => new ClientUserReportModel(report));
    }

    validateInitialData(response: ServerResponseData<ClientUsersResolverDataInterface>): void {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const messages = (response.data as ApiError[]).map((error: ApiError) => {
                if (error.status !== ClientUsersReportResolverErrorStatesEnum.GetReportFailed) {
                    return ClientUsersMessages.UnknownError.message;
                }

                return ClientUsersMessages.GetReportFailed.message;
            });

            throw new InitialComponentDataValidationException(messages);
        }
    }

    onRowClicked(rowId: string | number): void {
        const selectedClientUser = this.modelsCollection.find((item: ClientUserReportModel) => {
            return item.userId === rowId;
        });

        if (selectedClientUser) {
            this.router.navigate([
                this.panelPaths.root,
                this.panelPaths.children.clientUsers.path,
                this.panelPaths.children.clientUsers.children.edit.path,
                rowId,
                selectedClientUser.userTypeRelation,
                this.panelPaths.children.clientUsers.children.edit.children.details.path,
            ]).finally();
        }
    }
}
